import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  PlayArrow as RunIcon,
  RocketLaunch as ProvisionIcon,
  SkipNext as SkipIcon,
  WarningAmber as WarningIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/api';

const FALLBACK_STEPS = [
  {
    key: 'validate',
    step: '1. Validate signup & plan',
    description: 'Confirm environment, catalog products, ACR image pins, and target cluster'
  },
  {
    key: 'dns_ingress_tls',
    step: '2. DNS + Ingress + TLS',
    description: 'Open infra git branch, apply DNS/Ingress/TLS, verify records (pilvi-az-infra)'
  },
  {
    key: 'postgres',
    step: '3. Deploy Postgres',
    description: 'Provision empty tenant PostgreSQL (stock Postgres or thin base wrapper)'
  },
  {
    key: 'migrate_seed',
    step: '4. Migrations + seed',
    description: 'Run schema migrations and seed (admin users); optional golden dump later'
  },
  {
    key: 'deploy_apps',
    step: '5. Deploy FE/BE images',
    description: 'Deploy pinned erp4c FE/BE images with TENANT_ID, DB, domain, branding env'
  },
  {
    key: 'health',
    step: '6. Health checks',
    description: 'Smoke /health on frontend and backend until ready'
  },
  {
    key: 'finalize',
    step: '7. Mark ready + merge infra',
    description: 'Mark tenant provisioned and merge infra branch when DNS/TLS is healthy'
  }
];

const stepIcon = (status) => {
  switch (status) {
    case 'completed':
      return <CheckIcon color="success" />;
    case 'warning':
      return <WarningIcon color="warning" />;
    case 'running':
      return <CircularProgress size={22} />;
    case 'failed':
      return <ErrorIcon color="error" />;
    case 'skipped':
      return <SkipIcon color="disabled" />;
    default:
      return <PendingIcon color="disabled" />;
  }
};

const statusChip = (status) => {
  const map = {
    completed: { color: 'success', label: 'Completed' },
    warning: { color: 'warning', label: 'Completed with warnings' },
    running: { color: 'info', label: 'Running' },
    failed: { color: 'error', label: 'Failed' },
    skipped: { color: 'default', label: 'Skipped' },
    pending: { color: 'default', label: 'Pending' }
  };
  const c = map[status] || map.pending;
  return <Chip size="small" color={c.color} label={c.label} variant={status === 'skipped' ? 'outlined' : 'filled'} />;
};

/** Build human-readable detail bullets for each planned step from readiness.plan */
const buildStepDetails = (stepKey, plan = {}, mode = 'cloud') => {
  const details = [];
  if (stepKey === 'validate') {
    details.push(`Mode: ${mode}`);
    details.push(`Tenant ID: ${plan.tenant_id || '—'}`);
    details.push(`Environment: ${plan.environment || '—'} (${plan.environment_short_name || '—'})`);
    details.push(`Software shortname: ${plan.software_short_name || '—'}`);
    details.push(`Admins to seed: ${plan.admin_count ?? '—'}`);
    if (plan.cluster) {
      details.push(`Cluster: ${plan.cluster.label} (${plan.cluster.provider} · ${plan.cluster.cluster_name || 'n/a'})`);
    } else {
      details.push('Cluster: none (local docker-compose path)');
    }
  }
  if (stepKey === 'dns_ingress_tls') {
    const hosts = (plan.service_urls || []).map((u) => u.host);
    if (hosts.length) details.push(`Hosts: ${hosts.join(', ')}`);
    (plan.service_urls || []).forEach((u) => details.push(`${u.role}: ${u.url}`));
    details.push(`Infra branch: ${plan.infra_branch_name || '—'}`);
    details.push(`Namespace: ${plan.namespace || plan.container_prefix || '—'}`);
    details.push(
      plan.infra?.infra_git_configured
        ? `Infra git configured (${plan.infra.dns_base_domain}) — will open/apply branch`
        : `Infra git not configured — DNS plan recorded; set INFRA_GIT_REPO_URL for auto apply/merge`
    );
  }
  if (stepKey === 'postgres') {
    details.push(`Namespace: ${plan.namespace || plan.container_prefix || '—'}`);
    details.push(`DB pod: ${plan.workloads?.database || `${plan.container_prefix || '—'}-database`}`);
    details.push(`Database name: ${plan.database_name || '—'}`);
    details.push(`Database user: ${plan.database_user || '—'}`);
    details.push(`Database password: ${plan.database_password_set ? 'random (stored on tenant)' : 'will be generated'}`);
    details.push('Empty Postgres (not a live prod dump). Migrations/seed follow.');
  }
  if (stepKey === 'migrate_seed') {
    details.push(`Target DB: ${plan.database_name || '—'}`);
    details.push(`DB user: ${plan.database_user || '—'}`);
    details.push(`Seed ${plan.admin_count || 0} initial admin user(s)`);
    details.push('Schema via sequelize sync / migrations (golden dump optional later)');
  }
  if (stepKey === 'deploy_apps') {
    const images = plan.acr_images || [];
    if (images.length === 0) {
      details.push(mode === 'local_compose'
        ? 'Local compose will build images from Dockerfiles'
        : 'No ACR pins — cloud deploy cannot proceed');
    } else {
      images.forEach((img) => details.push(`Image: ${img.repository}:${img.tag}`));
    }
    if (plan.workloads?.frontend) details.push(`Frontend workload: ${plan.workloads.frontend}`);
    if (plan.workloads?.backend) details.push(`Backend workload: ${plan.workloads.backend}`);
    details.push(`Namespace: ${plan.namespace || plan.container_prefix || '—'}`);
    details.push('Inject TENANT_ID, DB connection, public domain, branding env');
  }
  if (stepKey === 'health') {
    const urls = (plan.service_urls || []).map((u) => u.url);
    if (urls.length) urls.forEach((u) => details.push(`Probe: ${u}`));
    else details.push('Probes deferred until public URLs / Ingress are live');
  }
  if (stepKey === 'finalize') {
    details.push('Set provisioning_status = completed');
    details.push('Record health_status and service URLs on the tenant');
    details.push(plan.infra?.infra_git_configured
      ? 'Merge infra branch when DNS/TLS healthy'
      : 'Manual merge of DNS/Ingress changes in pilvi-az-infra if needed');
  }
  // Repo selections — useful context on DNS/apps steps
  if (['dns_ingress_tls', 'deploy_apps'].includes(stepKey) && (plan.repository_selections || []).length) {
    plan.repository_selections.forEach((r) => {
      details.push(`Repo: ${r.organization || ''}/${r.name} @ ${r.selected_branch || 'default'}`);
    });
  }
  return details;
};

/**
 * Confirm dialog (what will run) → Start → right-hand live progress drawer → final result.
 */
const TenantProvisionWizard = ({
  tenantId,
  tenant,
  confirmOpen,
  onCloseConfirm,
  progressOpen,
  onOpenProgress,
  onCloseProgress,
  onTenantRefresh
}) => {
  const [readiness, setReadiness] = useState(null);
  const [loadingReadiness, setLoadingReadiness] = useState(false);
  const [starting, setStarting] = useState(false);
  const [runningStepKey, setRunningStepKey] = useState(null);
  const [selectedStepKeys, setSelectedStepKeys] = useState(() => FALLBACK_STEPS.map((s) => s.key));
  const [progressData, setProgressData] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);

  const loadReadiness = useCallback(async () => {
    if (!tenantId) return;
    setLoadingReadiness(true);
    try {
      const response = await api.get(`/provisioning/${tenantId}/readiness`);
      setReadiness(response.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load provision plan');
      setReadiness(null);
    } finally {
      setLoadingReadiness(false);
    }
  }, [tenantId]);

  const loadProgress = useCallback(async () => {
    if (!tenantId) return;
    try {
      const response = await api.get(`/provisioning/${tenantId}/progress`);
      setProgressData(response.data.data);
    } catch (err) {
      console.error('Failed to fetch provision progress', err);
    } finally {
      setProgressLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (confirmOpen) loadReadiness();
  }, [confirmOpen, loadReadiness]);

  useEffect(() => {
    if (!progressOpen) return undefined;
    setProgressLoading(true);
    loadProgress();
    const status = progressData?.provisioning_status || tenant?.provisioning_status;
    if (status !== 'in_progress') return undefined;
    const timer = setInterval(loadProgress, 2000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressOpen, tenant?.provisioning_status, progressData?.provisioning_status, loadProgress]);

  // Auto-open progress panel once when landing on an in-progress tenant
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (tenant?.provisioning_status === 'in_progress' && onOpenProgress && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      onOpenProgress();
    }
    if (tenant?.provisioning_status !== 'in_progress') {
      autoOpenedRef.current = false;
    }
  }, [tenant?.provisioning_status, onOpenProgress]);

  const plannedSteps = useMemo(() => {
    const fromApi = readiness?.cloud_steps?.length ? readiness.cloud_steps : FALLBACK_STEPS;
    const mode = readiness?.mode || 'cloud';
    const plan = readiness?.plan || {};
    return fromApi.map((s, index) => ({
      ...s,
      sequence: index + 1,
      details: buildStepDetails(s.key, plan, mode)
    }));
  }, [readiness]);

  // When plan loads, default-select every step
  useEffect(() => {
    if (!confirmOpen || !plannedSteps.length) return;
    setSelectedStepKeys(plannedSteps.map((s) => s.key).filter(Boolean));
  }, [confirmOpen, plannedSteps]);

  const toggleStep = (key) => {
    setSelectedStepKeys((prev) => (
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
  };

  const selectAllSteps = () => setSelectedStepKeys(plannedSteps.map((s) => s.key).filter(Boolean));
  const selectNoSteps = () => setSelectedStepKeys([]);

  const liveSteps = progressData?.steps?.length
    ? progressData.steps
    : (tenant?.provisioning_steps || []);

  const progressPercent = progressData?.progress_percent
    ?? (liveSteps.length
      ? Math.round((liveSteps.filter((s) =>
        s.status === 'completed' || s.status === 'warning' || s.status === 'skipped'
      ).length / liveSteps.length) * 100)
      : 0);
  const anyStepRunning = liveSteps.some((s) => s.status === 'running') || Boolean(runningStepKey);

  const provisionStatus = progressData?.provisioning_status || tenant?.provisioning_status;
  const isDone = provisionStatus === 'completed' || provisionStatus === 'failed';
  const prevStatusRef = useRef(provisionStatus);

  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = provisionStatus;
    if (prev === 'in_progress' && (provisionStatus === 'completed' || provisionStatus === 'failed')) {
      onTenantRefresh?.();
    }
  }, [provisionStatus, onTenantRefresh]);

  const handleConfirmStart = async () => {
    if (readiness && !readiness.ok) {
      toast.error((readiness.errors || []).join('; ') || 'Not ready to provision');
      return;
    }
    if (!selectedStepKeys.length) {
      toast.error('Select at least one step to run');
      return;
    }
    setStarting(true);
    try {
      const body = { steps: selectedStepKeys };
      if (tenant?.environment) body.environment = tenant.environment;
      const response = await api.post(`/provisioning/${tenantId}/start`, body);
      toast.success(response.data?.message || 'Provisioning started');
      onCloseConfirm?.();
      onOpenProgress?.();
      onTenantRefresh?.();
      setProgressLoading(true);
      await loadProgress();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start provisioning');
    } finally {
      setStarting(false);
    }
  };

  const handleRunSingleStep = async (stepKey) => {
    if (!stepKey || anyStepRunning) return;
    setRunningStepKey(stepKey);
    try {
      const body = { steps: [stepKey] };
      if (tenant?.environment) body.environment = tenant.environment;
      const response = await api.post(`/provisioning/${tenantId}/steps/run`, body);
      toast.success(response.data?.message || `Running ${stepKey}`);
      onOpenProgress?.();
      onTenantRefresh?.();
      setProgressLoading(true);
      await loadProgress();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to run step');
    } finally {
      setRunningStepKey(null);
    }
  };

  return (
    <>
      {/* ——— Confirm: what will run ——— */}
      <Dialog
        open={Boolean(confirmOpen)}
        onClose={() => !starting && onCloseConfirm?.()}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
          <ProvisionIcon color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={800}>Provision plan</Typography>
            <Typography variant="body2" color="text.secondary">
              Review every step before starting · {tenant?.tenant_name} ({tenant?.tenant_id})
            </Typography>
          </Box>
          <IconButton onClick={onCloseConfirm} disabled={starting} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingReadiness && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loadingReadiness && readiness && (
            <Stack spacing={2}>
              <Alert severity={readiness.ok ? 'info' : 'error'}>
                <Typography fontWeight={700}>
                  Mode: {readiness.mode}
                  {readiness.ok ? ' — ready to start' : ' — blocked'}
                </Typography>
                {!readiness.ok && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {(readiness.errors || []).join('; ')}
                  </Typography>
                )}
                {(readiness.warnings || []).length > 0 && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Warnings: {readiness.warnings.join('; ')}
                  </Typography>
                )}
              </Alert>

              {readiness.plan && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Inputs from Add Tenant
                  </Typography>
                  <Typography variant="body2">
                    Namespace: <code>{readiness.plan.namespace || readiness.plan.container_prefix}</code>
                  </Typography>
                  <Typography variant="body2">
                    DB: <code>{readiness.plan.database_name}</code>
                    {readiness.plan.database_user ? <> · user <code>{readiness.plan.database_user}</code></> : null}
                  </Typography>
                  <Typography variant="body2">
                    Infra branch: <code>{readiness.plan.infra_branch_name || '—'}</code>
                  </Typography>
                  <Typography variant="body2">
                    Cluster: {readiness.plan.cluster?.label || '— (local compose)'}
                  </Typography>
                  <Typography variant="body2">
                    Images: {(readiness.plan.acr_images || []).length
                      ? readiness.plan.acr_images.map((i) => `${i.repository}:${i.tag}`).join(', ')
                      : 'none (build from source if local)'}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" fontWeight={800} sx={{ flex: 1 }}>
                  Exact sequence ({plannedSteps.length} steps)
                </Typography>
                <Button size="small" onClick={selectAllSteps}>Select all</Button>
                <Button size="small" onClick={selectNoSteps}>Select none</Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Uncheck any step to skip it now. You can run skipped steps individually later from the progress panel.
              </Typography>
              <List disablePadding>
                {plannedSteps.map((s) => {
                  const checked = selectedStepKeys.includes(s.key);
                  return (
                    <ListItem
                      key={s.key || s.sequence}
                      alignItems="flex-start"
                      sx={{
                        mb: 1.25,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: checked ? 'primary.light' : 'divider',
                        bgcolor: checked ? '#fff' : 'grey.50',
                        opacity: checked ? 1 : 0.72,
                        flexDirection: 'column',
                        alignItems: 'stretch'
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1.5, width: '100%', alignItems: 'flex-start' }}>
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleStep(s.key)}
                          disabled={starting}
                          sx={{ mt: -0.5 }}
                          inputProps={{ 'aria-label': `Include ${s.step}` }}
                        />
                        <Chip
                          label={s.sequence}
                          color={checked ? 'primary' : 'default'}
                          size="small"
                          sx={{ fontWeight: 800, minWidth: 36, mt: 0.75 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={800}>{s.step}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {s.description}
                          </Typography>
                          {!checked && (
                            <Chip size="small" label="Will skip" variant="outlined" sx={{ mt: 0.75 }} />
                          )}
                          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                            {(s.details || []).map((d) => (
                              <Typography component="li" key={d} variant="body2" sx={{ mb: 0.35 }}>
                                {d}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>

              <Alert severity="warning">
                Starting will move this tenant to <strong>in progress</strong> and run the
                {' '}<strong>{selectedStepKeys.length}</strong> selected step(s)
                {selectedStepKeys.length < plannedSteps.length
                  ? ` (${plannedSteps.length - selectedStepKeys.length} skipped)`
                  : ''}.
                Watch live status in the panel on the right. Skipped steps can be run alone later.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onCloseConfirm} disabled={starting}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={starting ? <CircularProgress size={18} color="inherit" /> : <ProvisionIcon />}
            disabled={starting || loadingReadiness || (readiness && !readiness.ok) || !selectedStepKeys.length}
            onClick={handleConfirmStart}
            size="large"
          >
            {starting ? 'Starting…' : `Start processing (${selectedStepKeys.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ——— Live progress (right drawer) ——— */}
      <Drawer
        anchor="right"
        open={Boolean(progressOpen)}
        onClose={onCloseProgress}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420, md: 480 },
            p: 0,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #0ea5e9 140%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ opacity: 0.75 }}>Provisioning</Typography>
            <Typography variant="h6" fontWeight={800} noWrap>
              {tenant?.tenant_name || 'Tenant'}
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', opacity: 0.9 }}>
              {tenant?.tenant_id}
            </Typography>
          </Box>
          <IconButton onClick={loadProgress} sx={{ color: '#fff' }} aria-label="Refresh">
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={onCloseProgress} sx={{ color: '#fff' }} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 2.5, pt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="body2" color="text.secondary">Overall progress</Typography>
            <Typography variant="body2" fontWeight={800}>{progressPercent}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{
              height: 10,
              borderRadius: 5,
              mb: 1.5,
              '& .MuiLinearProgress-bar': {
                bgcolor: provisionStatus === 'failed'
                  ? 'error.main'
                  : provisionStatus === 'completed'
                    ? 'success.main'
                    : 'primary.main'
              }
            }}
          />
          <Chip
            size="small"
            label={provisionStatus || '—'}
            color={
              provisionStatus === 'completed' ? 'success'
                : provisionStatus === 'failed' ? 'error'
                  : provisionStatus === 'in_progress' ? 'warning'
                    : 'default'
            }
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, pb: 2 }}>
          {progressLoading && !liveSteps.length ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List disablePadding>
              {(liveSteps.length ? liveSteps : plannedSteps).map((step, index) => {
                const status = step.status || 'pending';
                const stepKey = step.key || FALLBACK_STEPS[index]?.key;
                const canRunAlone = Boolean(stepKey)
                  && liveSteps.length > 0
                  && status !== 'running'
                  && !anyStepRunning
                  && ['skipped', 'pending', 'failed', 'completed', 'warning'].includes(status)
                  && ['in_progress', 'completed', 'failed'].includes(provisionStatus);
                return (
                  <ListItem
                    key={stepKey || `${step.step}-${index}`}
                    alignItems="flex-start"
                    sx={{
                      mb: 1.25,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: status === 'running' ? 'primary.main' : status === 'skipped' ? 'grey.300' : 'divider',
                      bgcolor: status === 'running' ? 'action.hover' : 'background.paper',
                      boxShadow: status === 'running' ? 1 : 0,
                      opacity: status === 'skipped' ? 0.85 : 1
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                      {stepIcon(status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography fontWeight={700} variant="body2">{step.step}</Typography>
                          {statusChip(status)}
                        </Box>
                      )}
                      secondary={(
                        <Box sx={{ mt: 0.75 }}>
                          {step.description && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {step.description}
                            </Typography>
                          )}
                          {step.message && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {step.message}
                            </Typography>
                          )}
                          {step.error && (
                            <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                              {step.error}
                            </Alert>
                          )}
                          {canRunAlone && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={runningStepKey === stepKey
                                ? <CircularProgress size={14} />
                                : <RunIcon />}
                              sx={{ mt: 1 }}
                              disabled={anyStepRunning}
                              onClick={() => handleRunSingleStep(stepKey)}
                            >
                              {status === 'skipped' ? 'Run this step' : 'Re-run this step'}
                            </Button>
                          )}
                        </Box>
                      )}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        {/* Result footer */}
        {isDone && (
          <Box sx={{ p: 2.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            {provisionStatus === 'completed' ? (
              <Alert severity="success" sx={{ mb: 1.5 }}>
                <Typography fontWeight={700}>Provisioning finished</Typography>
                <Typography variant="body2">
                  {(progressData?.error || tenant?.provisioning_error)
                    ? `Completed with notes: ${progressData?.error || tenant?.provisioning_error}`
                    : 'Selected steps finished. Use “Run this step” above for any skipped steps, then activate when ready.'}
                </Typography>
                {(progressData?.infrastructure?.urls?.frontend || tenant?.frontend_url) && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.75, fontFamily: 'monospace' }}>
                    Frontend: {progressData?.infrastructure?.urls?.frontend || tenant?.frontend_url}
                  </Typography>
                )}
                {(progressData?.infrastructure?.urls?.backend || tenant?.backend_url) && (
                  <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                    Backend: {progressData?.infrastructure?.urls?.backend || tenant?.backend_url}
                  </Typography>
                )}
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                <Typography fontWeight={700}>Provisioning failed</Typography>
                <Typography variant="body2">
                  {progressData?.error || tenant?.provisioning_error || 'See failed step above'}
                </Typography>
              </Alert>
            )}
            <Button fullWidth variant="outlined" onClick={onCloseProgress}>
              Close
            </Button>
          </Box>
        )}
      </Drawer>
    </>
  );
};

export default TenantProvisionWizard;
